// PreviewTester/components/ScenarioBranch.js
import React, { useState, useEffect } from 'react';

const ScenarioBranch = ({
  scenario,
  index,
  hasActive,
  isElse,
  isElif,
  isActivePath,
  handleScenarioClick,
  renderScenarioTree,
  level = 0,
  forceCollapse = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Sync with forceCollapse prop (for Collapse All / Expand All)
  useEffect(() => {
    setIsCollapsed(forceCollapse);
  }, [forceCollapse]);
  
  const hasChildren = scenario.children && scenario.children.length > 0;

  const branchStyle = {
    marginBottom: '8px',
    padding: '8px',
    backgroundColor: isElse ? '#fefcbf' : 'white',
    borderRadius: '4px',
    border: isElse ? '1px solid #f6ad55' : '1px solid #e2e8f0',
    opacity: isActivePath ? 1 : 0.7,
    transition: 'all 0.2s ease'
  };

  const conditionTextStyle = {
    fontFamily: 'monospace',
    color: isElse ? '#744210' : '#2b6cb0',
    fontSize: '12px',
    backgroundColor: isElse ? '#fefcbf' : '#ebf8ff',
    padding: '2px 8px',
    borderRadius: '3px',
    wordBreak: 'break-all'
  };

  const buttonStyle = {
    padding: '4px 12px',
    backgroundColor: 'white',
    border: '1px solid #bee3f8',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#2b6cb0',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap'
  };

  const elseButtonStyle = {
    ...buttonStyle,
    border: '1px solid #f6ad55',
    backgroundColor: '#fefcbf',
    color: '#744210'
  };

  const label = isElse ? 'Else:' : isElif ? 'Elif:' : 'Condition:';

  const branchClasses = [
    'scenario-branch',
    isElse ? 'else-branch' : 'if-branch',
    isActivePath ? 'active-path' : 'inactive-path',
    `level-${level}`
  ].filter(Boolean).join(' ');

  return (
    <div
      key={scenario.id || index}
      style={branchStyle}
      className={branchClasses}
      data-condition={scenario.condition}
      data-level={level}
      data-is-else={isElse}
      data-is-active={isActivePath}
    >
      {/* Header row with collapse toggle */}
      <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {/* Collapse/Expand toggle button - only show if has children */}
        {hasChildren ? (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #cbd5e0',
              borderRadius: '3px',
              padding: '0px 4px',
              cursor: 'pointer',
              fontSize: '10px',
              color: '#4a5568',
              lineHeight: '18px',
              minWidth: '20px',
              textAlign: 'center',
              flexShrink: 0,
            }}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '▶' : '▼'}
          </button>
        ) : (
          <span style={{ width: '20px', flexShrink: 0 }} />
        )}
        
        <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </span>
        <span style={conditionTextStyle}>
          {scenario.condition}
        </span>
        {scenario.hasElse && !isElse && !isElif && (
          <span style={{ fontSize: '10px', backgroundColor: '#edf2f7', color: '#4a5568', padding: '1px 8px', borderRadius: '10px', fontWeight: '500' }}>
            + else
          </span>
        )}
        {hasChildren && (
          <span style={{ fontSize: '10px', backgroundColor: '#edf2f7', color: '#4a5568', padding: '1px 8px', borderRadius: '10px', fontWeight: '500' }}>
            {scenario.children.length} nested
          </span>
        )}
        {scenario.conditions && scenario.conditions.length > 1 && (
          <span style={{ fontSize: '10px', backgroundColor: '#edf2f7', color: '#4a5568', padding: '1px 8px', borderRadius: '10px', fontWeight: '500' }}>
            +{scenario.conditions.length - 1} more
          </span>
        )}
        {isActivePath && (
          <span style={{ fontSize: '10px', backgroundColor: '#4299e1', color: 'white', padding: '1px 8px', borderRadius: '10px', fontWeight: '500' }}>
            ▶ Active
          </span>
        )}
      </div>

      {/* Value buttons - always clickable, never disabled */}
      {scenario.values && scenario.values.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
          {scenario.values.map((value, vIdx) => {
            const isActive = value.isActive;
            let style;
            if (value.isElse) {
              style = elseButtonStyle;
            } else {
              style = buttonStyle;
            }

            const buttonClass = [
              'scenario-button',
              value.isElse ? 'else-button' : 'if-button',
              isActive ? 'active-button' : ''
            ].filter(Boolean).join(' ');

            return (
              <button
                key={vIdx}
                style={style}
                className={buttonClass}
                onClick={() => handleScenarioClick(value.dataUpdate, scenario.id, value)}
              >
                <span
                  style={{
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                  }}
                >
                  {value.label}
                  {isActive && ' ✓'}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Render children (nested conditions) - collapsible */}
            {/* Render children (nested conditions) - collapsible */}
      {hasChildren && !isCollapsed && (
        <div style={{ marginTop: '8px', paddingLeft: '16px', borderLeft: '2px solid #bee3f8' }}>
          <div style={{ fontSize: '11px', color: '#4a5568', marginBottom: '6px', fontWeight: '500' }}>
            {scenario.isElseBlock ? '└─ Nested Else Conditions:' : '└─ Nested Conditions:'}
          </div>
          {renderScenarioTree(scenario.children, level + 1, scenario.isElseBlock || isElse, isActivePath, forceCollapse)}
        </div>
      )}
      
      {/* Show collapsed indicator */}
      {hasChildren && isCollapsed && (
        <div 
          style={{ 
            marginTop: '4px', 
            paddingLeft: '16px', 
            fontSize: '10px', 
            color: '#a0aec0', 
            fontStyle: 'italic',
            cursor: 'pointer'
          }}
          onClick={() => setIsCollapsed(false)}
        >
          └─ {scenario.children.length} nested condition{scenario.children.length > 1 ? 's' : ''} (collapsed)
        </div>
      )}
    </div>
  );
};

export default ScenarioBranch;